import { useState } from 'react';
import { Plus, X, AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import toast from '@/hooks/useToast';

interface Interaction {
  drugs: string[];
  severity: 'HIGH' | 'MODERATE' | 'LOW';
  description: string;
  advice: string;
}

interface CheckResult {
  drugs: string[];
  interactions: Interaction[];
  safe: boolean;
  highRisk: boolean;
}

const SEVERITY_STYLES = {
  HIGH: 'bg-red-50 border-red-200 text-red-800',
  MODERATE: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  LOW: 'bg-blue-50 border-blue-200 text-blue-800',
};

const SEVERITY_ICONS = {
  HIGH: '🚨',
  MODERATE: '⚠️',
  LOW: 'ℹ️',
};

export const DrugCheckerPage = () => {
  const [drugs, setDrugs] = useState<string[]>(['', '']);
  const [result, setResult] = useState<CheckResult | null>(null);
  const [loading, setLoading] = useState(false);

  const addDrug = () => { if (drugs.length < 6) setDrugs(p => [...p, '']); };
  const removeDrug = (i: number) => setDrugs(p => p.filter((_, idx) => idx !== i));
  const updateDrug = (i: number, val: string) => setDrugs(p => p.map((d, idx) => idx === i ? val : d));

  const checkInteractions = async () => {
    const filled = drugs.filter(d => d.trim().length > 0);
    if (filled.length < 2) { toast.error('Enter at least 2 drug names'); return; }
    setLoading(true);
    try {
      const res = await api.post('/drug-checker/check', { drugs: filled });
      setResult(res.data.data);
    } catch { toast.error('Could not check interactions'); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
          <Shield className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h1 className="font-heading font-bold text-2xl text-gray-800">Drug Interaction Checker</h1>
          <p className="text-sm text-gray-500">Check if your medicines are safe to take together</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-sm text-blue-800">
        <strong>Disclaimer:</strong> This tool provides general information only. Always consult your pharmacist or doctor before changing your medication.
      </div>

      <div className="bg-white rounded-card shadow-card p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4">Enter Medicine Names</h2>
        <div className="space-y-3">
          {drugs.map((drug, i) => (
            <div key={i} className="flex gap-2">
              <Input
                placeholder={`Medicine ${i + 1} (e.g. Metformin, Aspirin)`}
                value={drug}
                onChange={e => updateDrug(i, e.target.value)}
                onKeyDown={e => e.key === 'Enter' && checkInteractions()}
              />
              {drugs.length > 2 && (
                <button onClick={() => removeDrug(i)} className="text-gray-400 hover:text-red-500 p-2">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-4">
          {drugs.length < 6 && (
            <button onClick={addDrug} className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium">
              <Plus className="h-4 w-4" /> Add another medicine
            </button>
          )}
          <Button className="ml-auto" loading={loading} onClick={checkInteractions}>
            Check Interactions
          </Button>
        </div>
      </div>

      {result && (
        <div className="bg-white rounded-card shadow-card p-6">
          {result.safe ? (
            <div className="flex items-center gap-3 text-green-700 bg-green-50 rounded-xl p-4 mb-4">
              <CheckCircle className="h-6 w-6 shrink-0" />
              <div>
                <p className="font-semibold">No known interactions found</p>
                <p className="text-sm">The medicines you entered appear safe to take together. Always confirm with your pharmacist.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-red-700 bg-red-50 rounded-xl p-4 mb-4">
              <AlertTriangle className="h-6 w-6 shrink-0" />
              <div>
                <p className="font-semibold">{result.highRisk ? '🚨 High Risk Interactions Found' : 'Interactions Found — Review Carefully'}</p>
                <p className="text-sm">Please consult your doctor or pharmacist before taking these together.</p>
              </div>
            </div>
          )}

          {result.interactions.map((interaction, i) => (
            <div key={i} className={`border rounded-xl p-4 mb-3 ${SEVERITY_STYLES[interaction.severity]}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{SEVERITY_ICONS[interaction.severity]}</span>
                <span className="font-semibold capitalize">{interaction.severity.toLowerCase()} Risk</span>
                <span className="text-xs ml-auto opacity-70">{interaction.drugs.join(' + ')}</span>
              </div>
              <p className="text-sm mb-2">{interaction.description}</p>
              <p className="text-sm font-medium">💡 {interaction.advice}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
